/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';


export interface SelectOption<T = string> {
  id: number;
  value: T;
  text: string;
  disabled?: boolean;
  optionElement?: JSX.Element;
}

interface SelectProps<T = string> {
  options: SelectOption<T>[];
  selectedId?: number;
  onChange?: (selected: SelectOption<T>) => void;
  onInputChange?: (value: string) => void;
  className?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  error?: boolean;
  helperText?: boolean | string;
  disabled?: boolean;
  name?: string;
}

export function ComboBoxField<T = string>({
  options,
  selectedId,
  onChange,
  onInputChange,
  className,
  variant = 'outlined',
  error,
  helperText,
  disabled,
  name,
}: Readonly<SelectProps<T>>) {
  const [id, setId] = useState<number>();
  const [inputValue, setInputValue] = useState('');

  const value = selectedId ?? id;
  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value]
  );

  useEffect(() => {
    if (value === undefined) {
      setInputValue('');
      setId(undefined);
    }
  }, [value]);

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.text);
    }
  }, [selectedOption]);

  function handleChange(
    _: SyntheticEvent,
    selected: SelectOption<T> | string | null
  ) {
    if (typeof selected === 'string') {
      if (selectedId === undefined) setId(undefined);
      setInputValue(selected);
      onInputChange?.(selected);
      return;
    }

    if (!selected) {
      if (selectedId === undefined) setId(undefined);
      setInputValue('');
      onInputChange?.('');
      return;
    }

    if (selectedId === undefined) {
      setId(selected.id);
    }

    setInputValue(selected.text);
    onInputChange?.(selected.text);
    onChange?.(selected);
  }

  function handleInputChange(_: SyntheticEvent, nextInputValue: string) {
    setInputValue(nextInputValue);
    if (selectedId === undefined) setId(undefined);
    onInputChange?.(nextInputValue);
  }

  return (
    <StyledAutocomplete
      freeSolo
      options={options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.text
      }
      isOptionEqualToValue={(option, currentValue) =>
        option.id === currentValue.id
      }
      getOptionDisabled={(option) => !!option.disabled}
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      noOptionsText="No values"
      fullWidth
      renderInput={(params) => (
        <StyledTextField
          {...params}
          error={error}
          variant={variant}
          disabled={disabled}
          name={name}
          helperText={helperText}
        />
      )}
      disabled={disabled}
      className={className}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.optionElement ?? option.text}
        </li>
      )}
    />
  );
}

const StyledAutocomplete = styled(Autocomplete<SelectOption<any>, false, false, true>)`
  width: 100%;
  & .MuiInputBase-root {
    height: 40px;
    align-items: center;
    font-size: ${({ theme }) => theme.fontSize.m};
    background-color: ${({ theme }) => theme.palette.background.default};
  }

  & .MuiInputBase-input {
    box-sizing: border-box;
    height: 100%;
    padding: 0 14px;
    line-height: 40px;
  }
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: ${({ theme }) => `2px solid ${theme.palette.primary.main}`};
  }

  & .MuiInputBase-root.Mui-disabled {
    color: ${({ theme }) => theme.palette.primary.main};
    background: ${({ theme, disabled }) =>
      disabled ? theme.palette.button.disabled : 'inherits'};
  }

  & fieldset {
    border: ${({ theme, disabled }) =>
      `${disabled ? '0px' : 'inherits'} solid ${theme.palette.primary.main}`};
  }
`;
